import React, { useState, useEffect } from 'react';
import { ReactPhotoSphereViewer as PhotoSphereViewer } from 'react-photo-sphere-viewer';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logOut } from '../features/auth/authSlice';
import { t } from 'i18next';

interface PhotoSphereViewerProps {
  image: string;
  title?: string;
  stationName:string;
}

const PhotoSphereViewerComponent: React.FC<PhotoSphereViewerProps> = ({ image, title, stationName }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [yaw, setYaw] = useState<number>(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        // Append token as a URL parameter
        const imageUrlWithToken = token ? `${image}?token=${encodeURIComponent(token)}` : image;

        const response = await fetch(imageUrlWithToken);
        if (!response.ok) throw new Error('Failed to fetch image');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);

        // Change the default 360 angle by stationName
        switch(stationName){
            case 'Tap Mun (W), 塔門(西)':
            case 'Tap Mun (E), 塔門(東)':
            case 'Butterfly Beach, 蝴蝶灣':
            case 'Pa Tau Kwu, 扒頭鼓':
            case 'Shuen Wan, 船灣':
              setYaw(2.5);
              break;
            default:
              setYaw(0);
                break;
        }

        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        setError(t('Failed to load panorama image'));
        alert(t('An error occurred. Please login again.'));
        dispatch(logOut());
        navigate('/');
      }
    };
    fetchImage();

    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl); // Clean up
    };
  }, [image]);

  if (error) {
    return <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>{t('Error')}: {error}</div>;
  }

  if (!blobUrl) {
    return <div>{t('Loading panorama')}...</div>;
  }

  return (
    <div style={{ width: '100%', height: '600px', position: 'relative' }}>
      {isLoading && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          {t('Loading panorama')}...
        </div>
      )}
      <PhotoSphereViewer
        src={blobUrl}
        height="100%"
        width="100%"
        navbar={['zoom', 'fullscreen']}
        caption={title}
        defaultYaw={yaw}
        defaultZoomLvl={1}
      />
    </div>
  );
};

export default PhotoSphereViewerComponent;